import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Paper,
  Stack,
  Chip,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsIcon from '@mui/icons-material/Sms';
import MessageIcon from '@mui/icons-material/Message';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MainLayout from '@/components/MainLayout';
import api from '@/lib/api';
import { toast } from '@/lib/toast';

// "Logged only" is a fact, not an emergency — mode-aware amber instead of
// a full-width warning Alert on every visit.
const mockColor = (t) => (t.palette.mode === 'dark' ? '#FFB74A' : '#B26A00');

const CHANNEL_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'email', label: 'Email', icon: <MailOutlineIcon sx={{ fontSize: 15 }} /> },
  { value: 'sms', label: 'SMS', icon: <SmsIcon sx={{ fontSize: 15 }} /> },
];

const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [formData, setFormData] = useState({
    recipient_id: '',
    channel: 'email',
    subject: '',
    content: '',
  });

  const fetchData = async () => {
    try {
      const [msgRes, memRes] = await Promise.all([api.get('/messages'), api.get('/members')]);
      setMessages(msgRes.data);
      setMembers(memRes.data);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/messages/send', formData);
      toast.success('Message sent successfully (mock)');
      setShowSendDialog(false);
      setFormData({ recipient_id: '', channel: 'email', subject: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const recipientName = useMemo(() => {
    const byId = new Map(members.map((m) => [m.member_id, m.name]));
    const byEmail = new Map(members.map((m) => [m.email, m.name]));
    const byPhone = new Map(members.map((m) => [m.phone, m.name]));
    return (msg) =>
      byId.get(msg.recipient_member_id) ||
      byEmail.get(msg.recipient_email) ||
      byPhone.get(msg.recipient_phone) ||
      msg.recipient_email ||
      msg.recipient_phone ||
      'Unknown';
  }, [members]);

  const visibleMessages = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return messages.filter((msg) => {
      if (channelFilter !== 'all' && msg.channel !== channelFilter) return false;
      if (!q) return true;
      return [recipientName(msg), msg.subject, msg.recipient_email, msg.recipient_phone, msg.content]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [messages, channelFilter, searchTerm, recipientName]);

  // The backend stores mock sends with a note; anything undelivered reads
  // "logged", real sends read "sent".
  const isMock = (msg) => Boolean(msg.note) || msg.status === 'mock';
  const anyMock = messages.some(isMock);

  return (
    <MainLayout title="Messages" badge={loading ? undefined : messages.length}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2.25 }}
        >
          <TextField
            placeholder="Search recipient or subject…"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="message-search-input"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            {CHANNEL_FILTERS.map((f) => {
              const selected = channelFilter === f.value;
              return (
                <Chip
                  key={f.value}
                  icon={f.icon}
                  label={f.label}
                  clickable
                  onClick={() => setChannelFilter(f.value)}
                  data-testid={`message-filter-${f.value}`}
                  sx={{
                    borderRadius: 999,
                    fontWeight: selected ? 600 : 500,
                    ...(selected
                      ? { bgcolor: 'primary.main', color: 'primary.contrastText', '& .MuiChip-icon': { color: 'primary.contrastText' }, '&:hover': { bgcolor: 'primary.main' } }
                      : { bgcolor: 'transparent', border: 1, borderColor: 'divider', color: 'text.secondary' }),
                  }}
                />
              );
            })}
          </Stack>
          {anyMock && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                border: 1,
                borderColor: (t) => alpha(mockColor(t), 0.45),
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: mockColor }} />
              <Typography variant="caption" fontWeight={500} sx={{ color: mockColor, whiteSpace: 'nowrap' }}>
                Mock mode — not delivered
              </Typography>
            </Stack>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowSendDialog(true)}
            data-testid="send-message-button"
            sx={{ flexShrink: 0 }}
          >
            Send message
          </Button>
        </Stack>

        {loading ? (
          <Skeleton variant="rounded" height={320} />
        ) : visibleMessages.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 8, textAlign: 'center' }}>
            <MessageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              {messages.length === 0 ? 'No messages yet' : 'No messages match your search'}
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {visibleMessages.map((msg, idx) => {
              const expanded = expandedId === msg.message_id;
              const mock = isMock(msg);
              return (
                <Box
                  key={msg.message_id}
                  data-testid={`message-${msg.message_id}`}
                  onClick={() => setExpandedId(expanded ? null : msg.message_id)}
                  sx={{
                    px: 2.5,
                    py: 1.625,
                    cursor: 'pointer',
                    borderBottom: idx === visibleMessages.length - 1 ? 0 : 1,
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 120ms ease',
                  }}
                >
                  {/* Phones: subject/preview wraps to its own full-width line. */}
                  <Stack direction="row" spacing={1.75} alignItems="center" sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: 0.5 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: (t) =>
                          msg.channel === 'email'
                            ? alpha(t.palette.primary.main, 0.14)
                            : alpha(t.palette.info.main, 0.14),
                        color: msg.channel === 'email' ? 'primary.main' : 'info.main',
                      }}
                    >
                      {msg.channel === 'email' ? (
                        <MailOutlineIcon sx={{ fontSize: 17 }} />
                      ) : (
                        <SmsIcon sx={{ fontSize: 17 }} />
                      )}
                    </Box>
                    <Box sx={{ width: { xs: 'auto', sm: 150 }, flex: { xs: 1, sm: '0 0 auto' }, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {recipientName(msg)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" noWrap display="block">
                        {msg.recipient_email || msg.recipient_phone}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ flex: { xs: '1 1 100%', sm: 1 }, minWidth: 0, order: { xs: 5, sm: 0 }, pl: { xs: '52px', sm: 0 } }}
                    >
                      {msg.subject && <Box component="span" fontWeight={600}>{msg.subject}</Box>}
                      <Box component="span" color="text.secondary">
                        {msg.subject ? ' — ' : ''}
                        {msg.content}
                      </Box>
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: (t) => (mock ? mockColor(t) : t.palette.success.main),
                        }}
                      />
                      <Typography variant="caption" fontWeight={500} color="text.secondary">
                        {mock ? 'logged' : 'sent'}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0, width: 56, textAlign: 'right' }}
                    >
                      {shortDate(msg.sent_at)}
                    </Typography>
                  </Stack>
                  <Collapse in={expanded}>
                    <Box sx={{ pl: '52px', pt: 1.25 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                        {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : ''}
                      </Typography>
                      {msg.note && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: mockColor }}>
                          {msg.note}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Paper>
        )}

        {anyMock && !loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.75, px: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">
              Delivery is off — connect Twilio &amp; SendGrid in Settings → Integrations to send for real.
            </Typography>
          </Stack>
        )}

        <Dialog open={showSendDialog} onClose={() => setShowSendDialog(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleSubmit}>
            <DialogTitle sx={{ fontWeight: 700 }}>Send message</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  select
                  required
                  label="Recipient"
                  fullWidth
                  value={formData.recipient_id}
                  onChange={(e) => setFormData({ ...formData, recipient_id: e.target.value })}
                >
                  {members.map((mem) => (
                    <MenuItem key={mem.member_id} value={mem.member_id}>
                      {mem.name} ({mem.email})
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Channel"
                  fullWidth
                  value={formData.channel}
                  onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                </TextField>
                {formData.channel === 'email' && (
                  <TextField
                    label="Subject"
                    required
                    fullWidth
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                )}
                <TextField
                  label="Message"
                  required
                  fullWidth
                  multiline
                  rows={5}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowSendDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained">
                Send
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default MessagesPage;
